/* eslint-disable @typescript-eslint/dot-notation */
import { useRouter } from 'next/router';
import { FC, useState, useEffect } from 'react';

import classNames from 'classnames';
import { SubmitHandler, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { Gift } from '@bpm-web-app/api-client';
import { set } from 'lodash';
import { errorToString, getCurrentPlatformLink, showToast } from '@bpm-web-app/utils';
import styles from './redeem.module.css';
import { Account, AccountPageProps } from '../account';
import { PageHeader } from '../components/page-header/page-header';
import { Button } from '../components/button/button';
import { Input } from '../components/form-fields/input/input';
import { Chip } from '../components/chip/chip';
import { ActionModal } from '../../shared/action-modal/action-modal';

type Props = AccountPageProps;

export const RedeemGiftPage: FC<Props> = ({ title }) => {
    const router = useRouter();
    const { code: queryCode } = router.query;

    const [loading, setLoading] = useState(false);
    const [redeemed, setRedeemed] = useState(false);
    const [giftCode, setGiftCode] = useState<Awaited<ReturnType<typeof Gift['validateGift']>>>();

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues:
        {
            code: '',
        },

        resolver: joiResolver(
            Joi.object({
                code: Joi.string().required().messages({
                    'string.empty': 'Please enter a gift card code.'
                }),
            })
        ),
    });

    useEffect(() => {
        if (queryCode && typeof queryCode === 'string') {
            setValue('code', queryCode);
        }
    }, [queryCode, setValue]);

    const submitForm: SubmitHandler<{ code: string; }> = async (data) => {
        setLoading(true);
        return Gift.validateGift(data.code).then((response) => {
            setGiftCode(response);
            return null;
        }).catch((error) => {
            if (error.status === 403) {
                showToast({ type: 'error', title: 'Gift card code not found. Please check the code and try again.' });
                return;
            }
            showToast({ type: 'error', title: errorToString(error) });
        }).finally(() => {
            setLoading(false);
        });
    };

    const redeemGift = async () => {
        if (giftCode) {
            setLoading(true);
            return Gift.redeeemGift(getValues('code')).then((response) => {
                setRedeemed(true);
                return null;
            }).catch((error) => {
                if (error.status === 403) {
                    showToast({ type: 'error', title: 'Gift card code not found. Please check the code and try again.' });
                    return;
                }
                showToast({ type: 'error', title: errorToString(error) });
            }).finally(() => {
                setLoading(false);
            });
        }
        return null;
    };

    return (
        <Account title="Redeem Gift Card">
            <PageHeader title="Redeem Gift Card" />
            <ActionModal
                title="Gift Credit Applied"
                subtitle={`$${giftCode?.data.amount} has been added to your account balance. This credit will be automatically applied to your next subscription purchase or, if you already have an active plan, to your upcoming renewal. Your balance will continue to be used until it’s fully exhausted, and you’ll only be charged if a plan costs more than your remaining credit.`}
                confirmButtonText="Go to My Account"
                hideCancel
                onConfirm={() => {
                    setRedeemed(false);
                    router.push(getCurrentPlatformLink('/account/overview'));
                }}
                onClose={() => {
                    setRedeemed(false);
                    router.push(getCurrentPlatformLink('/account/overview'));
                }}
                variant="light"
                isOpen={redeemed}
            />
            <div className={styles['redeem-container']}>
                <form onSubmit={handleSubmit(submitForm)}>
                    <div className={styles['redeem-details']}>
                        <div className={styles['redeem-header']}>
                            {!giftCode && (
                                <div className={styles['redeem-input']}>
                                    <Input
                                        label="Gift Card Code"
                                        type="string"
                                        {...register('code')}
                                        error={errors.code?.message}
                                    />
                                    <Button isLoading={loading} type="submit" variant="primary">Apply</Button>
                                </div>
                            )}
                            {giftCode && (
                                <div className={styles['redeem-success']}>
                                    <Chip
                                        label={getValues('code')}
                                        onRemove={() => {
                                            setValue('code', '');
                                            setGiftCode(undefined);
                                        }} />
                                    <p className={styles['redeem-success-description']}>${giftCode.data.amount} Off Selected Plan</p>
                                </div>
                            )}
                            <div className={styles['redeem-divider']} />
                            {giftCode && (
                                <h3 className={styles['redeem__pay-now']}>
                                    <span>Redeem Now:</span>
                                    <span>${giftCode?.data.amount} Credit</span>
                                </h3>
                            )}
                            <div className={styles['redeem-description']}>
                                Redeemed credit will be added to your account balance and can be used toward any BPM Supreme subscription.
                                If your selected plan costs more than your available credit, you’ll only pay the difference. Your account will not be charged until your gifted credit is exhausted.
                            </div>

                        </div>
                        <Button
                            variant="primary"
                            as="button"
                            type="button"
                            isLoading={loading}
                            disabled={!giftCode}
                            onClick={redeemGift}
                            className={classNames(styles['buy-button'])}
                        >Apply Gift Card
                        </Button>
                    </div>
                </form>
            </div>
        </Account>
    );
};
/* eslint-enable @typescript-eslint/dot-notation */
